import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { auth } from '../firebase';
import { authErrorMessage, isSilentAuthError } from '../authMessages';
import AuthLayout from '../components/AuthLayout';
import { toast } from 'react-toastify';
import GoogleLogo from '../images/google-g.webp';
import {
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useTheme } from '@mui/material/styles';
import { useSeo } from '../seo';

const Login = () => {
  const navigate = useNavigate();
  // The sign-up page sends people here, with the address they typed, when it
  // already has an account.
  const location = useLocation();
  const [email, setEmail] = useState(() => location.state?.email || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
      toast.success('Login successful!');
      navigate('/home');
    } catch (error) {
      toast.error(authErrorMessage(error));
    }
  };

  // Firebase emails the link and hosts the page that sets the new password.
  // Resetting also gives a password back to an account that lost it to "Sign
  // in with Google", and verifies the address so that cannot happen again.
  const handleForgotPassword = async () => {
    const address = email.trim();
    if (!address) {
      toast.info('Enter your email address above, then press "Forgot password?" again.');
      return;
    }
    setSendingReset(true);
    try {
      await sendPasswordResetEmail(auth, address, { url: `${window.location.origin}/login` });
      toast.success(
        `If there's an account for ${address}, we've emailed it a link to reset your password. ` +
          'Check your spam folder if it has not arrived in a few minutes.'
      );
    } catch (error) {
      toast.error(authErrorMessage(error));
    } finally {
      setSendingReset(false);
    }
  };

  const handleGoogleSignIn = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
      toast.success('Google sign-in successful!');
      navigate('/home');
    } catch (error) {
      if (!isSilentAuthError(error)) toast.error(authErrorMessage(error));
    }
  };

  useSeo({ title: 'Sign in | Crestancia', path: '/login', noindex: true });

  const theme = useTheme();
  const accent = theme.palette.primary.main;

  return (
    <AuthLayout title="Welcome back" subtitle="Sign in to place an order and track it.">
      <Box component="form" onSubmit={handleLogin} sx={{ width: '100%' }}>
        <TextField
          variant="outlined"
          required
          fullWidth
          id="email"
          label="Email Address"
          name="email"
          type="email"
          autoComplete="email"
          autoFocus
          inputProps={{ autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false }}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          sx={{ mb: 2.5 }}
        />
        <TextField
          variant="outlined"
          required
          fullWidth
          name="password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          id="password"
          autoComplete="current-password"
          inputProps={{ autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false }}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword((shown) => !shown)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  edge="end"
                  size="small"
                >
                  {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Button
            variant="text"
            size="small"
            onClick={handleForgotPassword}
            disabled={sendingReset}
            sx={{ textTransform: 'none', fontWeight: 600, color: accent, px: 1 }}
          >
            {sendingReset ? 'Sending…' : 'Forgot password?'}
          </Button>
        </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          disableElevation
          sx={{
            mt: 2,
            py: 1.5,
            borderRadius: 999,
            fontWeight: 700,
            fontSize: '1rem',
            textTransform: 'none',
          }}
        >
          Sign in
        </Button>

        <Divider sx={{ my: 3 }}>
          <Typography
            sx={{
              px: 1,
              fontSize: '0.72rem',
              fontWeight: 700,
              letterSpacing: '0.14em',
              color: 'text.secondary',
            }}
          >
            OR
          </Typography>
        </Divider>

        <Button
          fullWidth
          variant="outlined"
          onClick={handleGoogleSignIn}
          sx={{
            py: 1.4,
            borderRadius: 999,
            gap: 1.5,
            fontWeight: 600,
            textTransform: 'none',
            color: 'text.primary',
            borderColor: 'divider',
            '&:hover': { borderColor: 'text.disabled', backgroundColor: 'action.hover' },
          }}
        >
          <Box component="img" src={GoogleLogo} alt="" sx={{ height: 20, width: 20 }} />
          Sign in with Google
        </Button>
      </Box>

      <Typography sx={{ mt: 4, color: 'text.secondary', fontSize: '0.92rem' }}>
        Don&apos;t have an account?{' '}
        <Box
          component={Link}
          to="/signup"
          sx={{ color: accent, textDecoration: 'none', fontWeight: 700 }}
        >
          Sign up
        </Box>
      </Typography>
    </AuthLayout>
  );
};

export default Login;
